import { faPencil, faTimes } from "@fortawesome/free-solid-svg-icons";
import { DataTable } from "@ventumsoftware/react-toolkit";
import React, { type FC } from "react";
import { useApi } from "../../api/APIContext";
import type { CertificateType } from "../../api/dto";
import type { QuestionSet } from "../../api/dto/QuestionSet";
import { ContentContainer } from "../../components/content/ContentContainer";
import { PageHeader } from "../../components/content/PageHeader";
import { TablePlaceholder } from "../../components/data/TablePlaceholder";
import { Button, IconButton } from "../../components/form/Button";
import { Loading } from "../../components/Loading";
import { useModal } from "../../components/layout/ModalProvider";
import { Paragraph } from "../../components/Paragraph";
import { AddCertificateTypeModal } from "../../modals/AddCertificateTypeModal";
import { DeleteCertificateTypeModal } from "../../modals/DeleteCertificateTypeModal";
import { EditCertificateTypeModal } from "../../modals/EditCertificateTypeModal";

export const SettingsCertificationTypesPage: FC<{}> = (props) => {
  const { certificateTypes, questionSets, loading, refresh } = useApi();
  const addCertificateTypeModal = useModal({
    title: "Certificaat type toevoegen",
    body: <AddCertificateTypeModal questionSets={questionSets} />,
    size: "md",
  });
  return (
    <ContentContainer size={"lg"}>
      <div className={"flex items-center justify-between"}>
        <PageHeader>Certificeringstypes</PageHeader>
        <div>
          <Button
            type={"primary"}
            size={"md"}
            text={"Certificaat type toevoegen"}
            onClick={() => addCertificateTypeModal.open()}
          />
        </div>
      </div>
      <div className={"-mt-2 max-w-(--breakpoint-lg)"}>
        <Paragraph>
          In de namen van de certificeringstypes is het mogelijk om variabelen te gebruiken.
        </Paragraph>
        <table className={"-mt-2 mb-4 text-sm text-gray-700"}>
          <tbody>
            <tr>
              <th className={"text-left px-2"}>%C</th>
              <td>
                Wordt automatisch aangevuld met de landcode van de kandidaat (bijv. NL wanneer de
                kandidaat Nederlands is)
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <Loading loading={loading}>
        {certificateTypes.length === 0 ? (
          <TablePlaceholder>Geen certificaat types gevonden</TablePlaceholder>
        ) : (
          <DataTable
            keyProperty={"id"}
            data={certificateTypes}
            pageSize={50}
            columns={[
              {
                header: "Naam",
                sort: true,
                property: "name",
                transform: (value, row) => (
                  <div>
                    <div>{row.name}</div>
                  </div>
                ),
              },
              {
                header: "Set",
                property: "question_set_id",
                transform: (set_id) => {
                  const matchedSet = questionSets.find((qs) => qs.id === set_id);
                  if (matchedSet) {
                    return <div>{matchedSet.name}</div>;
                  } else {
                    return "-";
                  }
                },
              },
              {
                header: "Actief",
                sort: true,
                property: "is_active",
                transform: (is_active) => {
                  if (is_active) {
                    return (
                      <div className={"w-20"}>
                        <span className={"px-2 py-1 bg-green-600 rounded-full text-white"}>
                          Actief
                        </span>
                      </div>
                    );
                  } else {
                    return (
                      <div className={"w-20"}>
                        <span className={"px-2 py-1 bg-red-600 rounded-full text-white"}>
                          In-actief
                        </span>
                      </div>
                    );
                  }
                },
              },
              {
                header: "Acties",
                property: "id",
                transform: (_, row) => {
                  return (
                    <CertificateTypesTableControls
                      key={row.id}
                      certificateType={row}
                      questionSets={questionSets}
                    />
                  );
                },
              },
            ]}
          />
        )}
      </Loading>
    </ContentContainer>
  );
};

const CertificateTypesTableControls: FC<{
  certificateType: CertificateType;
  questionSets: QuestionSet[];
}> = (props) => {
  const editModal = useModal({
    title: "Certificaat type bewerken",
    body: (
      <EditCertificateTypeModal
        certificateType={props.certificateType}
        questionSets={props.questionSets}
      />
    ),
    size: "md",
  });
  const deleteModal = useModal({
    title: "Certificaat type verwijderen",
    body: <DeleteCertificateTypeModal certificateType={props.certificateType} />,
    size: "md",
  });
  return (
    <div className={"flex items-center justify-start space-x-4 -my-2"}>
      <IconButton type={"secondary"} size={"sm"} icon={faPencil} onClick={() => editModal.open()} />
      <IconButton type={"danger"} size={"sm"} icon={faTimes} onClick={() => deleteModal.open()} />
    </div>
  );
};
